import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Clock, Calendar } from "lucide-react";
import BlogShell from "@/pages/blog/BlogShell";
import SEO from "@/components/SEO";
import { getPostBySlug, getRelatedPosts, formatDate } from "@/pages/blog/posts";

/* ---------- inline markup helpers ---------- */

/** Renders **bold** and [label](url) inline markers in a plain string. */
function renderInline(text) {
  // Split by **...** first
  const boldSplit = text.split(/(\*\*[^*]+\*\*)/g);
  return boldSplit.map((chunk, i) => {
    if (chunk.startsWith("**") && chunk.endsWith("**")) {
      return (
        <strong key={i} className="text-ink font-semibold">
          {chunk.slice(2, -2)}
        </strong>
      );
    }
    // Then handle [label](url) inside remaining chunks
    const linkSplit = chunk.split(/(\[[^\]]+\]\([^)]+\))/g);
    return linkSplit.map((c, j) => {
      const m = c.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) {
        return (
          <a
            key={`${i}-${j}`}
            href={m[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-clay underline underline-offset-4 hover:text-ink"
          >
            {m[1]}
          </a>
        );
      }
      return <React.Fragment key={`${i}-${j}`}>{c}</React.Fragment>;
    });
  });
}

/* ---------- section renderers ---------- */

function Section({ node }) {
  switch (node.type) {
    case "h2":
      return (
        <h2 className="font-serif text-2xl sm:text-3xl text-ink mt-14 mb-4 leading-tight tracking-tight">
          {node.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-serif text-xl text-ink mt-10 mb-3 leading-snug">
          {node.text}
        </h3>
      );
    case "p":
      return (
        <p className="text-ink/85 text-[17px] leading-[1.8] mb-5">
          {renderInline(node.text)}
        </p>
      );
    case "ul":
      return (
        <ul className="list-disc pl-6 mb-6 space-y-2 text-ink/85 text-[17px] leading-[1.75] marker:text-clay">
          {node.items.map((it, i) => (
            // Static blog markdown — list never mutates at runtime.
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal pl-6 mb-6 space-y-2 text-ink/85 text-[17px] leading-[1.75] marker:text-clay marker:font-medium">
          {node.items.map((it, i) => (
            // Static blog markdown — list never mutates at runtime.
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    case "quote":
      return (
        <blockquote className="border-l-4 border-clay pl-5 py-2 my-8 italic text-ink/80 text-lg">
          "{node.text}"
          {node.cite && (
            <div className="not-italic text-sm text-ink-mid mt-2">— {node.cite}</div>
          )}
        </blockquote>
      );
    case "callout":
      return (
        <aside className="my-8 rounded-2xl bg-white border border-[#E2DCD0] p-6">
          {node.title && (
            <div className="text-xs uppercase tracking-[0.18em] text-clay font-semibold mb-2">
              {node.title}
            </div>
          )}
          <p className="text-ink/80 text-[16px] leading-relaxed m-0">
            {renderInline(node.text)}
          </p>
        </aside>
      );
    case "table":
      return (
        <div className="my-8 overflow-x-auto rounded-2xl border border-[#E2DCD0]">
          <table className="w-full text-[15px] text-left border-collapse">
            <thead className="bg-linen">
              <tr>
                {node.headers.map((h, i) => (
                  <th
                    key={`${h}-${i}`}
                    className="px-4 py-3 text-ink font-semibold border-b border-[#E2DCD0]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {node.rows.map((row, ri) => (
                // Static blog markdown rows — never reorder.
                // eslint-disable-next-line react/no-array-index-key
                <tr key={ri} className="odd:bg-white even:bg-linen/40">
                  {row.map((cell, ci) => (
                    <td
                      key={`${ri}-${ci}`}
                      className="px-4 py-3 text-ink/80 align-top border-b border-[#E2DCD0]/70 last:border-b-0"
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "cta":
      return (
        <div className="my-12 rounded-3xl bg-pine text-linen p-8 sm:p-10 text-center">
          <h3 className="font-serif text-2xl sm:text-3xl leading-tight tracking-tight">
            See what Viyora looks like at your clinic.
          </h3>
          <p className="mt-3 text-linen/75 text-[15px] max-w-xl mx-auto">
            30-day free trial. No credit card. Full Viyora Copilot, WhatsApp reminders and GST
            invoicing from day one.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/signup"
              data-testid="blog-inline-cta-primary"
              className="inline-flex items-center gap-2 rounded-full bg-linen text-ink px-6 py-3 text-sm font-semibold hover:bg-white transition"
            >
              Start 30-day trial <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:admin@viyora.co.in"
              data-testid="blog-inline-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border-2 border-linen/40 text-linen px-6 py-3 text-sm font-semibold hover:bg-linen/10 transition"
            >
              Book a demo
            </a>
          </div>
        </div>
      );
    default:
      return null;
  }
}

/* ---------- structured data for a post ---------- */
function articleLd(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "image": post.cover,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": { "@type": "Organization", "name": post.author },
    "publisher": {
      "@type": "Organization",
      "name": "Viyora Tech",
      "logo": {
        "@type": "ImageObject",
        "url": "https://viyora.co.in/viyora-icon-512.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://viyora.co.in/blog/${post.slug}`,
    },
    "keywords": post.keywords,
  };
}

/* ---------- page ---------- */
export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = getRelatedPosts(post.slug, 2);

  return (
    <BlogShell>
      <SEO
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.description}
        keywords={post.keywords}
        ogImage={post.cover}
        structuredData={articleLd(post)}
      />

      <article className="max-w-3xl mx-auto px-6 pt-14 pb-16" data-testid={`blog-post-${post.slug}`}>
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-ink-mid hover:text-ink mb-8"
          data-testid="blog-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All articles
        </Link>

        <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-4">
          {post.category}
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.1] tracking-tight">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-ink-mid">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> {formatDate(post.date)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {post.readMins} min read
          </span>
          <span>By {post.author}</span>
        </div>

        <div className="mt-10 rounded-2xl overflow-hidden border border-[#E2DCD0]">
          <img
            src={post.cover}
            alt={post.coverAlt}
            className="w-full h-64 sm:h-80 object-cover"
            loading="eager"
          />
        </div>

        <div className="mt-12">
          {post.sections.map((s, i) => (
            // Sections come from static blog post config — never reordered.
            // eslint-disable-next-line react/no-array-index-key
            <Section key={`${s.type}-${i}`} node={s} />
          ))}
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-24" data-testid="blog-related">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-6">
            Keep reading
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group rounded-2xl bg-white border border-[#E2DCD0] hover:border-pine transition overflow-hidden flex"
              >
                <div className="w-32 shrink-0 overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.coverAlt}
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[11px] text-clay uppercase tracking-wider font-medium mb-1.5">
                    {p.category}
                  </div>
                  <div className="font-serif text-lg text-ink leading-snug group-hover:text-clay transition">
                    {p.title}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </BlogShell>
  );
}
